'use client';

import {ReactNode, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import FormSection from '@/components/sections/form-section/FormSection';
import {ModalSectionItem} from '@/lib/ensure-link-button';
import {
  StyledModalBackdrop,
  StyledModalPanel,
  StyledModalSections,
  StyledModalCloseButton,
} from '@/components/ui/modal/PopupSectionModal.styles';

type PopupSectionModalProps = {
  open: boolean;
  onClose: () => void;
  sections: ReadonlyArray<ModalSectionItem | null>;
  ariaLabel?: string;
  labelledBy?: string;
};

// Renders a single modal section by its Contentful __typename. Unknown types
// render nothing so that adding section types in Contentful never crashes an
// older build.
function renderModalSection(section: ModalSectionItem): ReactNode {
  switch (section.__typename) {
    case 'FormulaSection':
      return <FormSection key={section.sys.id} section={section} />;
    default:
      return null;
  }
}

export default function PopupSectionModal({open, onClose, sections, ariaLabel, labelledBy}: PopupSectionModalProps) {
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;

    lastFocusedRef.current = document.activeElement as HTMLElement | null;
    const focusTimeout = window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    // Lock body scroll while the modal is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.clearTimeout(focusTimeout);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      lastFocusedRef.current?.focus();
    };
  }, [open, onClose]);

  // `open` only becomes true via a client-side interaction, so the portal is
  // never created during SSR. The `document` guard keeps this safe regardless.
  if (!open || typeof document === 'undefined') return null;

  const renderableSections = sections.filter((section): section is ModalSectionItem => section != null);

  return createPortal(
    <StyledModalBackdrop
      onClick={onClose}
      role='dialog'
      aria-modal='true'
      {...(labelledBy ? {'aria-labelledby': labelledBy} : {'aria-label': ariaLabel ?? 'Dialog'})}
    >
      <StyledModalPanel onClick={(e) => e.stopPropagation()}>
        <StyledModalCloseButton
          ref={closeButtonRef}
          type='button'
          onClick={onClose}
          aria-label='Close'
        >
          <CloseIcon fontSize='small' sx={{color: 'text.dark'}} />
        </StyledModalCloseButton>

        <StyledModalSections>
          {renderableSections.map(renderModalSection)}
        </StyledModalSections>
      </StyledModalPanel>
    </StyledModalBackdrop>,
    document.body,
  );
}
